import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, ShieldAlert, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import HelpGenieLogo from "@/components/ui/HelpGenieLogo";

const MAGENTA_BEAM = "#FF4DB8";
const SPARK_BLUE = "#4E9CFF";

export default function AuthConfirm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const tokenHash = searchParams.get("token_hash");
  const type = searchParams.get("type");
  const next = searchParams.get("next") || "/update-password";
  const handleConfirm = async () => {
    if (!tokenHash || !type) {
      setError("Invalid or missing reset token. Please request a new link.");
      return;
    }

    setLoading(true);
    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: type as "recovery" | "email",
      });

      if (verifyError) {
        setError(verifyError.message || "This link is invalid or expired.");
      } else {
        navigate(next);
      }
    } catch {
      setError("An error occurred while verifying your link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12"
      style={{
        background: `linear-gradient(135deg, ${MAGENTA_BEAM}08, white 50%, ${SPARK_BLUE}08)`,
      }}
    >
      <div className="flex justify-center mb-8">
        <HelpGenieLogo size={56} />
      </div>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-md p-8 md:p-10">
        {loading ? (
          <div className="text-center">
            <div className="mb-4 mx-auto w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center">
              <Loader2 className="h-6 w-6 text-genie-primary animate-spin" />
            </div>
            <h2
              className="text-2xl font-bold mb-2"
              style={{
                background: `linear-gradient(135deg, ${MAGENTA_BEAM}, ${SPARK_BLUE})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Verifying...
            </h2>
            <p className="text-gray-500 text-sm">Checking your reset link, one moment.</p>
          </div>
        ) : error ? (
          <div className="text-center">
            <div className="mb-4 mx-auto w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
              <ShieldAlert className="h-6 w-6 text-red-400" />
            </div>
            <h2
              className="text-2xl font-bold mb-3"
              style={{
                background: `linear-gradient(135deg, ${MAGENTA_BEAM}, ${SPARK_BLUE})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Link Expired
            </h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <Link to="/forgot-password">
              <Button className="bg-genie-primary hover:bg-genie-primary/90 text-white">
                Request a New Link
              </Button>
            </Link>
          </div>
        ) : !tokenHash || !type ? (
          <div className="text-center">
            <div className="mb-4 mx-auto w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
              <ShieldAlert className="h-6 w-6 text-red-400" />
            </div>
            <h2
              className="text-2xl font-bold mb-3"
              style={{
                background: `linear-gradient(135deg, ${MAGENTA_BEAM}, ${SPARK_BLUE})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Invalid Link
            </h2>
            <p className="text-gray-500 mb-6">Invalid or missing reset token. Please request a new link.</p>
            <Link to="/forgot-password">
              <Button className="bg-genie-primary hover:bg-genie-primary/90 text-white">
                Request a New Link
              </Button>
            </Link>
          </div>
        ) : (
          <div className="text-center">
            <div className="mb-4 mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <ShieldCheck className="h-6 w-6 text-genie-primary" />
            </div>
            <h2
              className="text-2xl font-bold mb-2"
              style={{
                background: `linear-gradient(135deg, ${MAGENTA_BEAM}, ${SPARK_BLUE})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Confirm Your Identity
            </h2>
            <p className="text-gray-500 text-sm mb-6">
              Click the button below to verify your identity and continue.
            </p>
            <Button
              onClick={handleConfirm}
              className="bg-genie-primary hover:bg-genie-primary/90 text-white w-full"
            >
              Verify & Continue
            </Button>
          </div>
        )}
      </div>

      <p className="mt-6 text-xs text-gray-400">
        &copy; {new Date().getFullYear()} Help Genie. All rights reserved.
      </p>
    </div>
  );
}
